/**
 * The one place a provider response stops being untyped.
 *
 * Every adapter used to carry its own copy of this block — a
 * `Record<string, unknown>` alias plus `asRecord` / `stringValue` / … taking
 * `unknown`. Eight copies meant eight boundaries, each re-deciding what a
 * decoded body is allowed to be. This module decides once: text goes in,
 * `JsonValue` comes out, and everything downstream reads a value that is known
 * to be JSON rather than one that is known to be nothing.
 *
 * `JsonValue` is deliberately *not* a validation layer. It says "this came
 * from JSON", not "this is a Pinterest pin" — the readers below narrow one
 * field at a time, and a provider that needs a real contract should parse with
 * a schema instead of walking records by hand.
 */

import type { JsonRecord, JsonValue } from "@postdaily/db";
import { z } from "zod";

// Defined in `packages/db` because the `jsonb` columns are what make the claim;
// re-exported here so provider code has one import for the type and the readers
// that narrow it.
export type { JsonRecord, JsonValue };

/**
 * `JsonValue` as a parser, for the boundaries that receive an already-decoded
 * body rather than text — a tRPC raw input, a queue message payload. Same claim
 * as {@link parseJson} ("this came from JSON"), reached without re-serializing
 * to run it back through `JSON.parse`.
 */
export const jsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(z.string(), jsonValueSchema),
  ]),
);

/**
 * Decode a response body. Returns `null` for anything that is not valid JSON,
 * which every caller already treats as "the provider sent us something we
 * cannot read".
 */
export function parseJson(text: string): JsonValue | null {
  try {
    // SAFETY: `JSON.parse` can only produce objects, arrays, strings, numbers,
    // booleans, or null — precisely the members of `JsonValue`. TypeScript
    // types the result `any`, so this assertion is the boundary that turns an
    // untyped decode into the domain's JSON type. It is the only place in the
    // provider layer that claim is made; every reader below re-checks the
    // individual field it wants.
    return JSON.parse(text) as JsonValue;
  } catch {
    return null;
  }
}

/**
 * The field readers below run a schema rather than probing the runtime shape.
 * Each one is shallow — it decodes the single field a caller asked for, not the
 * whole body — so reading `id` off a response never walks the payload beside it.
 */
const stringSchema = z.string();
const numberSchema = z.number();
const booleanSchema = z.boolean();

/**
 * A count a provider may send either as a number or as a decimal string
 * (YouTube and Pinterest both do the latter). `z.number()` rejects `NaN` and
 * `Infinity` on its own in Zod v4, so the numeric arm needs no extra guard.
 */
const countSchema = z.union([
  numberSchema,
  stringSchema
    .trim()
    .min(1)
    .transform((text) => Number(text))
    .refine((parsed) => Number.isFinite(parsed)),
]);

/**
 * Narrow to a JSON object — arrays and primitives are not records.
 *
 * This picks a member out of the `JsonValue` union rather than probing an
 * unparsed value: every non-record member is either nullish, an array, or a
 * primitive, so ruling those three out leaves exactly `JsonRecord` and
 * TypeScript narrows to it without an assertion.
 */
export function asRecord(value: JsonValue | undefined): JsonRecord | null {
  if (value === null || value === undefined || Array.isArray(value)) {
    return null;
  }

  return value instanceof Object ? value : null;
}

/** A JSON value with no structure inside it. */
export type JsonScalar = string | number | boolean | null;

/**
 * A JSON scalar: anything that is not an array or a nested object.
 *
 * Useful for "keep this one level deep" filters (audit metadata allowlists,
 * activity summaries) that would otherwise hand-roll four `typeof` checks.
 */
export function isJsonScalar(value: JsonValue): value is JsonScalar {
  return !Array.isArray(value) && asRecord(value) === null;
}

/** A string field, or `null` when absent or another type. */
export function stringValue(
  record: JsonRecord | null | undefined,
  key: string,
): string | null {
  const parsed = stringSchema.safeParse(record?.[key]);

  return parsed.success ? parsed.data : null;
}

/** A number field, or `null` when absent or another type. */
export function numberValue(
  record: JsonRecord | null | undefined,
  key: string,
): number | null {
  const parsed = numberSchema.safeParse(record?.[key]);

  return parsed.success ? parsed.data : null;
}

/**
 * A number read by position rather than by key, for the positional row matrices
 * the YouTube Analytics API returns (the column order comes from the `metrics`
 * the query asked for). Out-of-range indices and non-numeric cells both read as
 * `null` — a missing metric, not a zero.
 */
export function numberAt(
  values: readonly unknown[] | null | undefined,
  index: number,
): number | null {
  const parsed = numberSchema.safeParse(values?.[index]);

  return parsed.success ? parsed.data : null;
}

/** A boolean field, or `null` when absent or another type. */
export function booleanValue(
  record: JsonRecord | null | undefined,
  key: string,
): boolean | null {
  const parsed = booleanSchema.safeParse(record?.[key]);

  return parsed.success ? parsed.data : null;
}

/**
 * A `string[]` field, or `null` when absent or another type. Members that are
 * not strings are dropped.
 *
 * Exists because the per-item pass is where the ad-hoc versions across the
 * adapters disagreed: some dropped non-string members, some kept them, and one
 * returned the raw array untouched.
 */
export function stringArrayValue(
  record: JsonRecord | null | undefined,
  key: string,
): string[] | null {
  const value = record?.[key];

  if (!Array.isArray(value)) {
    return null;
  }

  return value.flatMap((item) => {
    const parsed = stringSchema.safeParse(item);

    return parsed.success ? [parsed.data] : [];
  });
}

/**
 * Coerce a count that a provider may send either as a number or as a decimal
 * string (YouTube and Pinterest both do the latter).
 */
export function parseCount(value: JsonValue | undefined): number | null {
  const parsed = countSchema.safeParse(value);

  return parsed.success ? parsed.data : null;
}

/**
 * Read a response body as JSON. Returns `null` when the provider did not send
 * JSON at all — an HTML error page, a gateway timeout, an empty body — which
 * every caller already treats the same as an unreadable payload.
 */
export async function readJsonBody(
  response: Response,
): Promise<JsonValue | null> {
  const contentType = response.headers.get("content-type") ?? "";

  if (!contentType.includes("application/json")) {
    return null;
  }

  try {
    // SAFETY: `Response.json()` resolves with the decoded body, which — like
    // `JSON.parse` above — can only be a JSON value. The runtime types it
    // `any`, so this is the boundary that names it; every reader narrows the
    // individual field it wants.
    return (await response.json()) as JsonValue;
  } catch {
    return null;
  }
}
